// src/components/practice/ExplanationPanel.jsx
//
// Collapsible "why is this the answer" panel, shared by the Lesen/Hören practice
// pages and the mock exam results review so both show explanations the same way.
// Explanations are stored as { de, en } (older Hören probes store a plain German
// string); the viewer's language is preferred, then English, then German.
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Lightbulb } from 'lucide-react';

export function explanationText(explanation, language) {
  if (!explanation) return '';
  if (typeof explanation === 'string') return explanation;
  const lang = (language || 'en').split('-')[0];
  return explanation[lang] || explanation.en || explanation.de || '';
}

const ExplanationPanel = ({ explanation }) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const { t, i18n } = useTranslation();
  const text = explanationText(explanation, i18n.language);
  if (!text) return null;
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className={`min-h-[44px] inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl border transition-all duration-300 touch-manipulation select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
          open
            ? 'bg-amber-50 border-amber-300 text-amber-700 shadow-[0_6px_16px_rgba(251,191,36,0.2)]'
            : 'bg-white border-black/10 text-gray-500 hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50/60'
        }`}
      >
        <Lightbulb className="w-3.5 h-3.5" />
        {open
          ? t('exam.training.hideExplanation', { defaultValue: 'Hide explanation' })
          : t('exam.training.showExplanation', { defaultValue: 'Show explanation' })}
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div id={panelId} className="mt-2 flex gap-2.5 p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 leading-relaxed animate-fade-in">
          <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="whitespace-pre-line">{text}</p>
        </div>
      )}
    </div>
  );
};

export default ExplanationPanel;
